import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Smartphone } from "lucide-react";
import { useHostname, useReferrer } from "@/lib/ssr";
import { isNewHomeEnabled } from "@/lib/homeFlag";
import { SEARCH_API_ENDPOINT, getApiBaseDomain } from "@/lib/apiDomain";
import { APP_PATH } from "@/pages/home/paths";
import { initialPromoState, promoContext, resolvedPromoState, type PromoState } from "@/content/help/promo";

// The Photohawk app card on consumer ("Find & download your photos") help articles. Shown in photohawk.com's own context
// and to Lite/free workspaces' buyers only: never in a Pro or Max workspace's context (src/content/help/promo.ts has
// the rule and how the context is found). Also hidden wherever the app page itself isn't live (the new site's
// /personal: src/lib/homeFlag.ts), so it never links to a 404.

/** Looks the workspace's plan up (anonymous public tenant record). undefined = the lookup failed. */
const fetchTenant = async (hostname: string, signal: AbortSignal): Promise<unknown> => {
  try {
    const res = await fetch(`${SEARCH_API_ENDPOINT}/albums?${new URLSearchParams({ hostname })}`, { signal });
    if (!res.ok) return undefined;
    const body = (await res.json()) as { tenant?: unknown };
    return body?.tenant;
  } catch {
    return undefined;
  }
};

const useAppPromoState = (): PromoState => {
  const host = useHostname();
  const referrer = useReferrer();
  const { search } = useLocation();
  const ctx = useMemo(() => promoContext(search, referrer, getApiBaseDomain()), [search, referrer]);
  const [state, setState] = useState<PromoState>(() => initialPromoState(ctx));

  useEffect(() => {
    setState(initialPromoState(ctx));
    if (ctx.kind === "none") return;
    const ac = new AbortController();
    fetchTenant(ctx.hostname, ac.signal).then((tenant) => {
      if (!ac.signal.aborted) setState(resolvedPromoState(tenant));
    });
    return () => ac.abort();
  }, [ctx]);

  return isNewHomeEnabled(host) ? state : "hide";
};

const HelpAppPromo = () => {
  const { t } = useTranslation();
  const state = useAppPromoState();
  if (state !== "show") return null;
  return (
    <aside data-testid="help-app-promo" aria-label={t("help.promo.eyebrow")} className="my-10 max-w-[68ch] overflow-hidden rounded-2xl border border-hawk-orange/30 bg-gradient-to-br from-[#FFF4EC] to-white p-6 dark:from-hawk-navy dark:to-hawk-navy">
      <div className="flex gap-4">
        <div aria-hidden="true" className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-hawk-orange to-hawk-gold text-white">
          <Smartphone size={22} />
        </div>
        <div className="min-w-0">
          <p className="m-0 text-xs font-bold uppercase tracking-[0.12em] text-[hsl(18_100%_38%)]">{t("help.promo.eyebrow")}</p>
          <p className="mb-1 mt-1 font-display text-lg font-bold leading-snug">{t("help.promo.title")}</p>
          <p className="mb-4 mt-0 text-sm text-foreground/80">{t("help.promo.body")}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to={APP_PATH} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-hawk-orange to-hawk-gold px-5 py-2.5 text-sm font-semibold text-white hover:opacity-95">
              {t("help.promo.cta")}
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <span className="text-xs text-muted-foreground">{t("help.promo.soon")}</span>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default HelpAppPromo;
